import { doc, getDoc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { db } from '../../../firebase';
import type { OcdTheme, OcdThemesData } from './settingsTypes';

export class OcdThemesService {
  private static instance: OcdThemesService;
  private readonly COLLECTION = 'metadata';
  private readonly COLLECTION_PRIVATE = 'metadata_private';
  private readonly DOC_ID = 'ocd_themes';

  private constructor() {}

  public static getInstance(): OcdThemesService {
    if (!OcdThemesService.instance) {
      OcdThemesService.instance = new OcdThemesService();
    }
    return OcdThemesService.instance;
  }

  public async getOcdThemes(): Promise<OcdTheme[]> {
    const docRef = doc(db, this.COLLECTION_PRIVATE, this.DOC_ID);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data() as OcdThemesData;
      return data.ocd_themes;
    }

    return [];
  }

  public async updateOcdThemes(themes: OcdTheme[]): Promise<void> {
    const transaction = writeBatch(db);
    const publicDocRef = doc(db, this.COLLECTION, this.DOC_ID);
    const privateDocRef = doc(db, this.COLLECTION_PRIVATE, this.DOC_ID);
    transaction.set(privateDocRef, { ocd_themes: themes, updatedAt: serverTimestamp() }, { merge: true });
    const publicThemes = themes.map((theme) => ({
      key: theme.key,
      friendlyName: theme.friendlyName,
      order: theme.order,
      isPublished: theme.isPublished,
    }));
    transaction.set(publicDocRef, { ocd_themes: publicThemes, updatedAt: serverTimestamp() }, { merge: true });
    await transaction.commit();
  }

  public async addOcdTheme(theme: OcdTheme): Promise<void> {
    const themes = await this.getOcdThemes();

    // Check if theme with this key already exists
    if (themes.some((t) => t.key === theme.key)) {
      throw new Error(`Theme with key "${theme.key}" already exists`);
    }

    themes.push(theme);
    await this.updateOcdThemes(themes);
  }

  public async updateOcdTheme(key: string, updates: Partial<Omit<OcdTheme, 'key'>>): Promise<void> {
    const themes = await this.getOcdThemes();
    const index = themes.findIndex((t) => t.key === key);

    if (index === -1) {
      throw new Error(`Theme with key "${key}" not found`);
    }

    themes[index] = { ...themes[index], ...updates };
    await this.updateOcdThemes(themes);
  }

  public async deleteOcdTheme(key: string): Promise<void> {
    const themes = await this.getOcdThemes();
    const filteredThemes = themes.filter((t) => t.key !== key);

    if (filteredThemes.length === themes.length) {
      throw new Error(`Theme with key "${key}" not found`);
    }

    await this.updateOcdThemes(filteredThemes);
  }

  public async reorderOcdThemes(themes: OcdTheme[]): Promise<void> {
    // Update order property for each theme
    const reorderedThemes = themes.map((theme, index) => ({
      ...theme,
      order: index,
    }));

    await this.updateOcdThemes(reorderedThemes);
  }
}

export const ocdThemesService = OcdThemesService.getInstance();
